import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FolderHeart, Heart } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Spinner } from '@/components/ui/spinner'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { useAuth } from '@/hooks/use-auth'
import { ensureFavouritesCollection, listCollections } from '@/lib/collections'
import { callableErrorMessage } from '@/lib/callable-errors'
import { relativeTime } from '@/lib/utils'
import type { PropertyCollection } from '@/types/models'

export function CollectionsPage() {
  return (
    <RequireAuth>
      <CollectionsHome />
    </RequireAuth>
  )
}

function CollectionsHome() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [collections, setCollections] = useState<PropertyCollection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        await ensureFavouritesCollection(user.uid)
        const list = await listCollections(user.uid)
        if (!cancelled) setCollections(list)
      } catch (e) {
        if (!cancelled) setError(callableErrorMessage(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [user])

  return (
    <div className="flex w-full flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold text-navy">Collections</h1>
        <p className="mt-1 text-sm text-muted">
          Save properties you like and organise them into collections.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <p className="text-sm text-danger">{error}</p>
      ) : collections.length === 0 ? (
        <EmptyState
          title="No collections yet"
          description="Open a property and tap the heart or plus to save it."
          actionLabel="Search properties"
          onAction={() => navigate('/search')}
        />
      ) : (
        <ul className="space-y-3">
          {collections.map((c) => (
            <li key={c.id}>
              <Link to={`/collections/${c.id}`} className="block">
                <Card className="transition hover:border-biddle-blue">
                  <CardContent className="flex items-center gap-3 pt-5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-biddle-blue/10 text-biddle-blue">
                      {c.isFavourites ? (
                        <Heart className="h-5 w-5 fill-biddle-blue" />
                      ) : (
                        <FolderHeart className="h-5 w-5" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-navy">{c.name}</p>
                      {c.description ? (
                        <p className="mt-0.5 truncate text-sm text-muted">{c.description}</p>
                      ) : null}
                      <p className="mt-1 text-sm text-muted">
                        {c.itemCount} {c.itemCount === 1 ? 'property' : 'properties'}
                        {c.updatedAt ? ` · Updated ${relativeTime(c.updatedAt)}` : null}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
